import React, { memo } from 'react';
import classNames from 'classnames';
import styles from './index.module.less';
import { BaseImage } from '../BaseImage';
import { getImagePath } from '@shared/utils/image/url';
import { NewBaseIcon } from '@shared/components/base/NewBaseIcon';
import { compressImage } from '@shared/utils/image';
import { hsbToRgb } from '@shared/utils/color';
import emojiRegex from 'emoji-regex';
interface BaseAvatarProps {
	name?: string;
	userId?: string;
	textSize?: number;
	size?: number;
	hasHover?: boolean;
	imageSrc?: string;
	className?: string;
	style?: React.CSSProperties;
	/** mask遮罩层中的icon */
	triggerIcon?: React.ReactNode;
	/** hover出现遮罩层 */
	hasMask?: boolean;
	isMobile?: boolean;
	onClick?: (e?: React.MouseEvent<HTMLElement>) => void;
	bottomRightIcon?: string;
}

export const getUserAvatarUrl = (imgSrc?: string) => {
	if (!imgSrc) return;

	if (imgSrc === '-') {
		return '-';
	} else if (/^https?|^data:image/.test(imgSrc)) {
		return imgSrc;
	} else {
		return getImagePath('userAvatar', imgSrc);
	}
};
/**
 * @param name 根据名称获取展示的首字母，支持emoji符号显示
 * @returns
 */
const getInitials = (name: string): string => {
	const regex = emojiRegex();
	const match = name.match(regex);
	if (!name.trim()) {
		return 'A';
	}
	if (match) {
		// 如果字符串包含emoji表情符号，直接返回匹配到的表情符号
		return match[0];
	}

	// 否则，返回字符串的第一个字符，并转为大写
	return name.trim()[0].toUpperCase();
};

/**
 * @param userId 根据userId生成0-360范围内的随机数，颜色取值范围 H: 0-360 （随机取数）S: 75 B: 90
 * @returns
 */
const getBackgroundColor = (userId: string): string => {
	if (!userId) {
		return `rgb(249, 250, 251)`;
	}
	const prime = 2027;
	const num = parseInt(userId.slice(-10)) || 1;
	const hash = (num * prime) % 361;
	const rgb = hsbToRgb(hash, 75, 90);
	const [r, g, b] = rgb;
	return `rgb(${r}, ${g}, ${b})`;
};

const BaseAvatarV2: React.FC<BaseAvatarProps> = ({
	size = 44,
	imageSrc,
	name,
	textSize,
	userId,
	className,
	style,
	triggerIcon,
	bottomRightIcon,
	isMobile = false,
	hasHover = false,
	hasMask = false,
	onClick,
}) => {
	const defaultTriggerIcon = (
		<span
			className={classNames(styles.base_avatar, styles.base_avatar_with_name)}
			style={{
				backgroundColor: getBackgroundColor(userId || '') || 'rgb(242, 244, 247)',
				fontSize: `${textSize ? textSize : size / 2}px`,
			}}
		>
			{name && getInitials(name)}
		</span>
	);
	return (
		<div
			className={classNames(
				{
					[styles.avatar_container]: true,
					[styles.mask_container]: hasMask,
					[styles.mask_hasHover]: hasHover,
				},
				className,
			)}
			style={{ width: `${size}px`, height: `${size}px`, ...style }}
			onClick={onClick}
		>
			{imageSrc && imageSrc !== '-' ? (
				<BaseImage
					src={compressImage(getUserAvatarUrl(imageSrc) || '', {
						resize: isMobile ? '100' : '500',
					})}
					alt="avatar"
					className={styles.base_avatar}
					errorComponent={defaultTriggerIcon}
					loadingComponent={defaultTriggerIcon}
				/>
			) : (
				defaultTriggerIcon
			)}
			{hasMask && <div className={styles.avatar_mask}>{triggerIcon}</div>}
			{/* 右下角icon */}
			{bottomRightIcon && (
				<div className={styles.br_icon}>
					<NewBaseIcon icon={bottomRightIcon} size={18}></NewBaseIcon>
				</div>
			)}
		</div>
	);
};

export default memo(BaseAvatarV2);
